import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faHandHoldingDollar, faBan } from '@fortawesome/free-solid-svg-icons';
import { RegistroAquisicao, podeSerLiquidado, podeSerCancelado, RegistroDetalhe, computeDisplayStatus, registrosService } from '../../services/registrosService';
import LiquidarParcelasModal, { ParcelaParaLiquidar } from '../LiquidarParcelasModal/LiquidarParcelasModal';
import DataTable, { ColumnDef } from '../../../../../components/DataTable';

interface RegistrosTableProps {
    data: RegistroAquisicao[];
    loading?: boolean;
    onSort: (field: string) => void;
    sortField: string;
    sortDirection: 'asc' | 'desc';
    onLiquidar?: (contratoIds: number[]) => void;
    onLiquidarParcelas?: (parcelas: Array<{ id_parcela: number; vl_pago: number; dtMovimento: string }>) => Promise<void>;
    onCancelar?: (contratoIds: number[]) => void;
    onRefresh?: () => void;
}

const RegistrosTable: React.FC<RegistrosTableProps> = ({ 
    data, 
    loading = false, 
    onSort, 
    sortField, 
    sortDirection,
    onLiquidar,
    onLiquidarParcelas,
    onCancelar,
    onRefresh
}) => {
    const [selectedRegistro, setSelectedRegistro] = useState<RegistroAquisicao | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedContratos, setSelectedContratos] = useState<number[]>([]);
    const [selectedParaCancelar, setSelectedParaCancelar] = useState<number[]>([]);
    const [selectedParcelas, setSelectedParcelas] = useState<number[]>([]);
    const [showLiquidarParcelasModal, setShowLiquidarParcelasModal] = useState(false);
    const [parcelasCarregadas, setParcelasCarregadas] = useState<RegistroDetalhe[]>([]);
    const [loadingParcelas, setLoadingParcelas] = useState(false);

    useEffect(() => {
        if (selectedRegistro) {
            const atualizado = data.find(r => r.idContrato === selectedRegistro.idContrato);
            if (atualizado) {
                setSelectedRegistro(atualizado);
            }
        }
        const validIds = new Set(
            data.filter(item => podeSerLiquidado(computeDisplayStatus(item) as any)).map(item => item.idContrato)
        );
        setSelectedContratos(prev => prev.filter(id => validIds.has(id)));
    }, [data]);

    const handleViewDetails = async (registro: RegistroAquisicao) => {
        setSelectedRegistro(registro);
        setParcelasCarregadas([]);
        setIsModalOpen(true);
        try {
            setLoadingParcelas(true);
            const parcelas = await registrosService.fetchParcelas(registro.idContrato);
            setParcelasCarregadas(parcelas);
        } catch {
            setParcelasCarregadas([]);
        } finally {
            setLoadingParcelas(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedRegistro(null);
        setSelectedParcelas([]);
        setParcelasCarregadas([]);
        setShowLiquidarParcelasModal(false);
    };

    const parcelaPodeLiquidar = (status: string) => {
        return status !== 'LIQUIDADO' && status !== 'ERRO' && status !== 'REJEITADO' && status !== 'RECUSADO' && status !== null && status !== undefined;
    };

    const handleSelectParcela = (parcelaId: number) => {
        const parcela = parcelasCarregadas.find(d => d.id_parcela === parcelaId);
        if (!parcela || !parcelaPodeLiquidar(parcela.status)) return;
        setSelectedParcelas(prev => {
            if (prev.includes(parcelaId)) {
                return prev.filter(id => id !== parcelaId);
            } else {
                return [...prev, parcelaId];
            }
        });
    };

    const handleSelectAllParcelas = (parcelas: RegistroDetalhe[]) => {
        const liquidaveis = parcelas.filter(p => parcelaPodeLiquidar(p.status));
        if (selectedParcelas.length === liquidaveis.length && liquidaveis.length > 0) {
            setSelectedParcelas([]);
        } else {
            setSelectedParcelas(liquidaveis.map(p => p.id_parcela));
        }
    };

    const handleLiquidarParcelasClick = () => {
        if (!selectedRegistro || selectedParcelas.length === 0) return;
        setShowLiquidarParcelasModal(true);
    };

    const handleConfirmLiquidarParcelas = async (parcelas: Array<{ id_parcela: number; vl_pago: number; dtMovimento: string }>) => {
        if (!onLiquidarParcelas) return;
        await onLiquidarParcelas(parcelas);
        setSelectedParcelas([]);
        if (selectedRegistro) {
            try {
                setLoadingParcelas(true);
                const updatedParcelas = await registrosService.fetchParcelas(selectedRegistro.idContrato);
                setParcelasCarregadas(updatedParcelas);
            } catch {
            } finally {
                setLoadingParcelas(false);
            }
        }
    };

    const handleSelectContrato = (contratoId: number, status: string, displayStatus: string) => {
        if (!podeSerLiquidado(displayStatus as any)) return;
        
        setSelectedContratos(prev => {
            if (prev.includes(contratoId)) {
                return prev.filter(id => id !== contratoId);
            } else {
                return [...prev, contratoId];
            }
        });
    };

    const handleSelectAll = () => {
        const liquidableContratos = data.filter(item => podeSerLiquidado(computeDisplayStatus(item) as any));
        if (selectedContratos.length === liquidableContratos.length && liquidableContratos.length > 0) {
            setSelectedContratos([]);
        } else {
            setSelectedContratos(liquidableContratos.map(item => item.idContrato));
        }
    };

    const handleLiquidar = () => {
        if (onLiquidar && selectedContratos.length > 0) {
            onLiquidar(selectedContratos);
        }
    };

    const handleSelectParaCancelar = (contratoId: number, displayStatus: string) => {
        if (!podeSerCancelado(displayStatus as any)) return;
        setSelectedParaCancelar(prev =>
            prev.includes(contratoId) ? prev.filter(id => id !== contratoId) : [...prev, contratoId]
        );
    };

    const handleSelectAllParaCancelar = () => {
        const cancelaveis = data.filter(item => podeSerCancelado(computeDisplayStatus(item) as any));
        if (selectedParaCancelar.length === cancelaveis.length && cancelaveis.length > 0) {
            setSelectedParaCancelar([]);
        } else {
            setSelectedParaCancelar(cancelaveis.map(item => item.idContrato));
        }
    };

    const handleCancelar = () => {
        if (onCancelar && selectedContratos.length > 0) {
            onCancelar(selectedContratos);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        if (dateString.length === 10 && dateString.includes('-')) {
            const [year, month, day] = dateString.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('pt-BR');
        }
        
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const getStatusClass = (status: string) => {
        switch(status) {
            case 'ATIVO':
            case 'Aceita':                
            case 'REGISTRADO':         
                return 'status-ativo';
            case 'RECUSADO':
            case 'ERRO':                  
            case 'REJEITADO':            
                return 'status-recusado';
            case 'CANCELADO':
                return 'status-cancelado';
            case 'LIQUIDADO':
                return 'status-liquidado';
            case 'LIQUIDADO_PARCIALMENTE': 
                return 'status-ativo';
            case 'NAO_REGISTRADO':
                return 'status-nao-registrado';    
            default:
                return '';
        }
    };

    const registrosColumns = useMemo((): ColumnDef<RegistroAquisicao>[] => [
        { key: 'idContrato',       label: 'ID',               sortable: true },
        { key: 'numeroContrato',   label: 'Nº Contrato',       sortable: true },
        { key: 'carteira',         label: 'Carteira',          sortable: true },
        { key: 'originador',       label: 'Originador',        sortable: true },
        { key: 'cedente',          label: 'Cedente',           sortable: true },
        {
            key: 'saldoRegistrado',
            label: 'Saldo Registrado',
            sortable: true,
            cellClassName: 'currency-cell',
            render: item => formatCurrency(item.saldoRegistrado),
        },
        {
            key: 'dtAquisicao',
            label: 'Data Aquisição',
            sortable: true,
            render: item => formatDate(item.dtAquisicao),
        },
        {
            key: 'status',
            label: 'Status',
            sortable: true,
            cellClassName: item => `status-cell ${getStatusClass(computeDisplayStatus(item))}`,
            render: item => {
                const ds = computeDisplayStatus(item);
                return (
                    <span
                        className="status-badge"
                        title={ds === 'RECUSADO' && item.mensagem ? item.mensagem : undefined}
                        style={ds === 'RECUSADO' && item.mensagem ? { cursor: 'help' } : undefined}
                    >
                        {ds}
                    </span>
                );
            },
        },
        { key: 'qtdParcelas',  label: 'Qtd Parcelas', sortable: true },
        { key: 'registradora', label: 'Registradora', sortable: true },
        { key: 'tipoRecebivel', label: 'Tipo recebível', sortable: true },
    ], []);

    const parcelasColumns = useMemo((): ColumnDef<RegistroDetalhe>[] => [
        { key: 'id_parcela',     label: 'ID' },
        { key: 'numero_parcela', label: 'Parcela' },
        { key: 'seu_numero',     label: 'Seu Número' },
        {
            key: 'data_vencimento',
            label: 'Data Vencimento',
            render: d => formatDate(d.data_vencimento),
        },
        {
            key: 'saldo_registrado',
            label: 'Saldo Registrado',
            cellClassName: 'currency-cell',
            render: d => formatCurrency(d.saldo_registrado ?? d.valor_nominal),
        },
        {
            key: 'status',
            label: 'Status',
            cellClassName: d => `status-cell ${getStatusClass(d.status)}`,
            render: d => {
                const isRecusado = d.status === 'ERRO' || d.status === 'REJEITADO' || d.status === 'RECUSADO';
                return (
                    <span
                        className="status-badge"
                        title={isRecusado && d.mensagem ? d.mensagem : undefined}
                        style={isRecusado && d.mensagem ? { cursor: 'help' } : undefined}
                    >
                        {getStatusClass(d.status) === 'status-recusado' ? 'RECUSADO' : d.status}
                    </span>
                );
            },
        },
    ], []);

    return (
        <div className="results-section">
            {loading && (
                <div className="loading-state">
                    <p>Carregando registros...</p>
                </div>
            )}

            {!loading && data && data.length > 0 && (
                <div className="liquidar-actions-bar">
                    <span className="liquidar-selected-count">
                        {selectedContratos.length > 0
                            ? `${selectedContratos.length} ${selectedContratos.length === 1 ? 'contrato selecionado' : 'contratos selecionados'}`
                            : 'Nenhum contrato selecionado'}
                    </span>
                    <button
                        className="liquidar-button"
                        onClick={handleLiquidar}
                        title={selectedContratos.length > 0 ? 'Liquidar contratos selecionados' : 'Selecione contratos para liquidar'}
                        disabled={selectedContratos.length === 0}
                    >
                        <FontAwesomeIcon icon={faHandHoldingDollar} style={{ marginRight: '8px' }} />
                        Liquidar Contratos
                    </button>
                    <button
                        className="cancelar-button"
                        onClick={handleCancelar}
                        title={selectedContratos.length > 0 ? 'Cancelar registros selecionados' : 'Selecione registros para cancelar'}
                        disabled={selectedContratos.length === 0}
                    >
                        <FontAwesomeIcon icon={faBan} style={{ marginRight: '8px' }} />
                        Cancelar Registros
                    </button>
                </div>
            )}
            {!loading && (
                <DataTable<RegistroAquisicao>
                    data={data}
                    columns={registrosColumns}
                    loading={false}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={onSort}
                    rowKey={(item, index) => `${item.idContrato}-${index}`}
                    rowClassName={item =>
                        !podeSerLiquidado(computeDisplayStatus(item) as any)
                            ? 'row-not-liquidable'
                            : ''
                    }
                    rowSelection={{
                        selectedIds: selectedContratos,
                        onSelect: item =>
                            handleSelectContrato(item.idContrato, item.status ?? '', computeDisplayStatus(item)),
                        onSelectAll: handleSelectAll,
                        isSelectable: item =>
                            podeSerLiquidado(computeDisplayStatus(item) as any),
                        getRowId: item => item.idContrato,
                        disabledTitle: 'Contrato não pode ser liquidado',
                    }}
                    renderActions={item =>
                        item.qtdParcelas > 0 ? (
                            <button
                                className="view-button"
                                onClick={() =>
                                    computeDisplayStatus(item) !== 'RECUSADO' &&
                                    handleViewDetails(item)
                                }
                                title={
                                    computeDisplayStatus(item) === 'RECUSADO'
                                        ? 'Contrato recusado'
                                        : 'Ver parcelas do contrato'
                                }
                                disabled={computeDisplayStatus(item) === 'RECUSADO'}
                                style={
                                    computeDisplayStatus(item) === 'RECUSADO'
                                        ? { opacity: 0.35, cursor: 'not-allowed' }
                                        : undefined
                                }
                            >
                                <FontAwesomeIcon icon={faEye} />
                            </button>
                        ) : null
                    }
                    loadingText="Carregando registros..."
                    emptyTitle="Nenhum registro encontrado"
                    emptyMessage="Não há registros disponíveis com os filtros aplicados."
                    emptyHint="Tente ajustar os filtros ou limpar para ver todos os registros."
                />
            )}
            {isModalOpen && selectedRegistro && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" style={{ width: '80vw', maxWidth: '95vw' }} onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Detalhes do Registro #{selectedRegistro.idContrato}</h3>
                            <button className="modal-close" onClick={closeModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="registro-info">
                                <div className="info-row">
                                    <span className="info-label">Contrato:</span>
                                    <span className="info-value">{selectedRegistro.numeroContrato}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Carteira:</span>
                                    <span className="info-value">{selectedRegistro.carteira}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Fundo:</span>
                                    <span className="info-value">{selectedRegistro.fundo}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Originador:</span>
                                    <span className="info-value">{selectedRegistro.originador}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">CNPJ Originador:</span>
                                    <span className="info-value">{selectedRegistro.cnpjOriginador}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Cedente:</span>
                                    <span className="info-value">{selectedRegistro.cedente}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">CNPJ Cedente:</span>
                                    <span className="info-value">{selectedRegistro.cnpjCedente}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Data Aquisição:</span>
                                    <span className="info-value">{formatDate(selectedRegistro.dtAquisicao)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Saldo Registrado:</span>
                                    <span className="info-value">{formatCurrency(selectedRegistro.saldoRegistrado)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Valor Atual:</span>
                                    <span className="info-value">{formatCurrency(selectedRegistro.valorAtual)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Valor Nominal Total:</span>
                                    <span className="info-value">{formatCurrency(selectedRegistro.valorNominalTotal)}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Qtd Parcelas:</span>
                                    <span className="info-value">{selectedRegistro.qtdParcelas}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Registradora:</span>
                                    <span className="info-value">{selectedRegistro.registradora}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">ID Registradora:</span>
                                    <span className="info-value">{selectedRegistro.idRegistradoraContrato}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">IPOC:</span>
                                    <span className="info-value">{selectedRegistro.ipoc}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Tipo Recebível:</span>
                                    <span className="info-value">{selectedRegistro.tipoRecebivel}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">Status:</span>
                                    <span
                                        className={`info-value ${getStatusClass(computeDisplayStatus(selectedRegistro))}`}
                                        title={
                                            computeDisplayStatus(selectedRegistro) === 'RECUSADO' && selectedRegistro.mensagem
                                                ? selectedRegistro.mensagem
                                                : undefined
                                        }
                                        style={
                                            computeDisplayStatus(selectedRegistro) === 'RECUSADO' && selectedRegistro.mensagem
                                                ? { cursor: 'help' }
                                                : undefined
                                        }
                                    >
                                        {computeDisplayStatus(selectedRegistro)}
                                    </span>
                                </div>
                            </div>

                            <div className="detalhes-header">
                                <h4 className="detalhes-title">Parcelas</h4>
                                <button
                                    className="liquidar-parcelas-button"
                                    onClick={handleLiquidarParcelasClick}
                                    title={
                                        selectedParcelas.length > 0
                                            ? 'Liquidar parcelas selecionadas'
                                            : 'Selecione parcelas para liquidar'
                                    }
                                    disabled={selectedParcelas.length === 0}
                                >
                                    <FontAwesomeIcon icon={faHandHoldingDollar} style={{ marginRight: '8px' }} />
                                    {selectedParcelas.length > 0
                                        ? `Liquidar ${selectedParcelas.length} ${selectedParcelas.length === 1 ? 'Parcela' : 'Parcelas'}`
                                        : 'Liquidar Parcelas'}
                                </button>
                            </div>

                            <DataTable<RegistroDetalhe>
                                data={parcelasCarregadas}
                                columns={parcelasColumns}
                                loading={loadingParcelas}
                                loadingText="Carregando parcelas..."
                                rowKey={(d, idx) => `${selectedRegistro.idContrato}-${d.id_parcela}-${idx}`}
                                rowClassName={d =>
                                    !parcelaPodeLiquidar(d.status) ? 'row-not-liquidable' : ''
                                }
                                rowSelection={{
                                    selectedIds: selectedParcelas,
                                    onSelect: d => handleSelectParcela(d.id_parcela),
                                    onSelectAll: () =>
                                        handleSelectAllParcelas(parcelasCarregadas),
                                    isSelectable: d => parcelaPodeLiquidar(d.status),
                                    getRowId: d => d.id_parcela,
                                    disabledTitle: 'Parcela não pode ser liquidada',
                                }}
                                className="detalhes-table-container"
                                emptyTitle="Nenhuma parcela encontrada"
                                emptyMessage="Este registro não possui parcelas."
                            />
                        </div>
                    </div>
                </div>
            )}

            {showLiquidarParcelasModal && selectedRegistro && (
                <LiquidarParcelasModal
                    show={showLiquidarParcelasModal}
                    onClose={() => setShowLiquidarParcelasModal(false)}
                    onConfirm={handleConfirmLiquidarParcelas}
                    onSuccess={onRefresh}
                    parcelas={
                        parcelasCarregadas
                            .filter(d => selectedParcelas.includes(d.id_parcela))
                            .map(d => ({
                                id_parcela: d.id_parcela,
                                parcela: d.numero_parcela.toString(),
                                seuNumero: d.seu_numero,
                                saldo_registrado: d.saldo_registrado ?? d.valor_nominal,
                                vl_pago: (d.saldo_registrado ?? d.valor_nominal).toFixed(2),
                            }))}
                />
            )}
        </div>
    );
};

export default RegistrosTable;
